import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Trash2, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvoiceForm = ({ shipment, existingInvoice, onSuccess, onCancel }) => {
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const [customerName, setCustomerName] = useState(existingInvoice?.customer_name || shipment?.receiver?.name || "");
  const [items, setItems] = useState(existingInvoice?.items || [
    { description: "", weight: "", volume: "", price: 0, fixed_fee: 0, total: 0 }
  ]);
  const [shippingFee, setShippingFee] = useState(existingInvoice?.shipping_fee || 0);
  const [discount, setDiscount] = useState(existingInvoice?.discount || 0);
  const [notes, setNotes] = useState(existingInvoice?.notes || "");

  const isEditing = !!existingInvoice;
  const shipmentId = existingInvoice?.shipment_id || shipment?.id;

  // Calculate item total: (Weight + Volume) × Price + Fixed Fee
  const calculateItemTotal = (item) => {
    const weight = parseFloat(item.weight) || 0;
    const volume = parseFloat(item.volume) || 0;
    const price = parseFloat(item.price) || 0;
    const fixedFee = parseFloat(item.fixed_fee) || 0;
    return (weight + volume) * price + fixedFee;
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  };

  const calculateGrandTotal = () => {
    const subtotal = calculateSubtotal();
    return Math.max(subtotal + (parseFloat(shippingFee) || 0) - (parseFloat(discount) || 0), 0);
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    newItems[index].total = calculateItemTotal(newItems[index]);
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { description: "", weight: "", volume: "", price: 0, fixed_fee: 0, total: 0 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const invoiceData = {
      customer_name: customerName,
      items: items.map(item => ({
        description: item.description,
        weight: item.weight ? parseFloat(item.weight) : null,
        volume: item.volume ? parseFloat(item.volume) : null,
        price: parseFloat(item.price) || 0,
        fixed_fee: parseFloat(item.fixed_fee) || 0,
        total: calculateItemTotal(item)
      })),
      shipping_fee: parseFloat(shippingFee) || 0,
      discount: parseFloat(discount) || 0,
      notes: notes || null
    };

    try {
      if (isEditing) {
        await axios.put(`${API}/invoices/${existingInvoice.id}`, invoiceData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Invoice updated successfully!");
      } else {
        await axios.post(`${API}/invoices`, { ...invoiceData, shipment_id: shipmentId }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Invoice created successfully!");
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save invoice");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header Info */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest">Customer Name</Label>
          <Input
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            placeholder="Customer name"
            required
            data-testid="invoice-customer-name"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest">Shipment</Label>
          <Input
            value={shipment?.tracking_number || existingInvoice?.invoice_number || "N/A"}
            disabled
            className="bg-slate-100"
          />
        </div>
      </div>

      {/* Invoice Items */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-xs uppercase tracking-widest font-bold">Invoice Items</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addItem}
            className="text-xs"
          >
            <Plus className="w-3 h-3 mr-1" /> Add Item
          </Button>
        </div>

        <div className="space-y-3">
          {items.map((item, index) => (
            <div key={index} className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-12 sm:col-span-4">
                  <Label className="text-[10px] uppercase text-slate-500">Description</Label>
                  <Input
                    value={item.description}
                    onChange={(e) => updateItem(index, "description", e.target.value)}
                    placeholder="Item description"
                    className="h-9"
                    required
                  />
                </div>
                <div className="col-span-4 sm:col-span-1">
                  <Label className="text-[10px] uppercase text-slate-500">Weight (lbs)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={item.weight}
                    onChange={(e) => updateItem(index, "weight", e.target.value)}
                    className="h-9"
                  />
                </div>
                <div className="col-span-4 sm:col-span-1">
                  <Label className="text-[10px] uppercase text-slate-500">Vol (ft³)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={item.volume}
                    onChange={(e) => updateItem(index, "volume", e.target.value)}
                    className="h-9"
                  />
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <Label className="text-[10px] uppercase text-slate-500">Price ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={item.price}
                    onChange={(e) => updateItem(index, "price", e.target.value)}
                    className="h-9"
                  />
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <Label className="text-[10px] uppercase text-slate-500">Fixed Fee ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={item.fixed_fee}
                    onChange={(e) => updateItem(index, "fixed_fee", e.target.value)}
                    className="h-9"
                  />
                </div>
                <div className="col-span-3 sm:col-span-1">
                  <Label className="text-[10px] uppercase text-slate-500">Total</Label>
                  <div className="h-9 flex items-center font-mono text-sm font-bold text-slate-700">
                    ${calculateItemTotal(item).toFixed(2)}
                  </div>
                </div>
                <div className="col-span-1 flex items-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeItem(index)}
                    className="text-red-500 hover:text-red-700 h-9 px-2"
                    disabled={items.length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Totals Section */}
      <div className="bg-slate-900 text-white p-6 rounded-lg">
        <div className="grid grid-cols-2 gap-4 max-w-md ml-auto">
          <div className="text-slate-400 text-sm">Subtotal:</div>
          <div className="text-right font-mono font-bold">${calculateSubtotal().toFixed(2)}</div>
          
          <div className="text-slate-400 text-sm">Shipping Fee:</div>
          <div className="text-right">
            <Input
              type="number"
              step="0.01"
              value={shippingFee}
              onChange={(e) => setShippingFee(e.target.value)}
              className="h-8 w-28 text-right font-mono bg-slate-800 border-slate-700 text-white ml-auto"
              data-testid="invoice-shipping-fee"
            />
          </div>
          
          <div className="text-slate-400 text-sm">Discount:</div>
          <div className="text-right">
            <Input
              type="number"
              step="0.01"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              className="h-8 w-28 text-right font-mono bg-slate-800 border-slate-700 text-white ml-auto"
              data-testid="invoice-discount"
            />
          </div>
          
          <div className="text-amber-500 font-bold text-lg border-t border-slate-700 pt-3">Grand Total:</div>
          <div className="text-right font-mono font-black text-2xl text-amber-500 border-t border-slate-700 pt-3">
            ${calculateGrandTotal().toFixed(2)}
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-widest">Notes (Optional)</Label>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Additional notes..."
          rows={2}
        />
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          type="submit"
          disabled={loading}
          className="flex-1 h-12 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold uppercase tracking-widest"
          data-testid="save-invoice-btn"
        >
          <FileText className="w-4 h-4 mr-2" />
          {loading ? "Saving..." : isEditing ? "Update Invoice" : "Create Invoice"}
        </Button>
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="h-12"
          >
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
};

export default InvoiceForm;
